import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Clock, Download, Play, Plus, Save, Trash2, X } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select } from '@/components/ui/misc';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  OPERATOR_LABELS,
  downloadReportCsv,
  operatorsFor,
  useDeleteReport,
  useDeleteSchedule,
  useReportSchedules,
  useReportSources,
  useRunReport,
  useSaveReport,
  useSaveSchedule,
  useSavedReports,
  type FilterOperator,
  type ReportDefinition,
  type ReportFilter,
  type ReportSource,
  type SavedReport,
} from './api';

const CHART_COLOURS = ['#1e40af', '#0891b2', '#16a34a', '#ca8a04', '#7c3aed', '#db2777'];

/**
 * The custom report builder (spec RPT-04).
 *
 * Everything offered here comes from the server's catalogue: the sources, the
 * fields on each, and which comparisons a field's type allows. Nothing the
 * browser sends is trusted as a column name — it is looked up again before it
 * reaches a query.
 */
export function ReportsPage() {
  const { data: sources } = useReportSources();
  const { data: saved } = useSavedReports();

  const [definition, setDefinition] = useState<ReportDefinition>({ dataSource: '', fields: [] });
  const [chartType, setChartType] = useState('table');
  const [savingAs, setSavingAs] = useState(false);
  const [scheduling, setScheduling] = useState<SavedReport | null>(null);
  const [loadedId, setLoadedId] = useState<string | null>(null);

  const run = useRunReport();
  const removeReport = useDeleteReport();

  const source = (sources ?? []).find((item) => item.key === definition.dataSource);

  // Choosing a source starts a fresh report on the first few columns rather
  // than an empty table with a "pick some columns" nag.
  useEffect(() => {
    if (!source) return;
    setDefinition((current) =>
      current.fields.length > 0
        ? current
        : { ...current, fields: source.fields.slice(0, 5).map((field) => field.key) },
    );
  }, [source]);

  const loadSaved = (report: SavedReport) => {
    setLoadedId(report.id);
    setChartType(report.chartType);
    setDefinition({
      dataSource: report.dataSource,
      fields: report.fields,
      filters: report.filters ?? [],
      groupBy: report.groupBy ?? undefined,
      aggregates: report.aggregates ?? [],
      sort: report.sort ?? undefined,
    });
    run.mutate({
      dataSource: report.dataSource,
      fields: report.fields,
      filters: report.filters ?? [],
      groupBy: report.groupBy ?? undefined,
      aggregates: report.aggregates ?? [],
      sort: report.sort ?? undefined,
    });
  };

  const canRun = Boolean(definition.dataSource && definition.fields.length > 0);

  return (
    <div>
      <PageHeader
        title="Reports"
        description="Build a report from any of the data you already have access to, then save it or send it on a schedule."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={!canRun}
              onClick={() =>
                void downloadReportCsv(definition, `${definition.dataSource || 'report'}.csv`)
              }
            >
              <Download className="me-1.5 h-3.5 w-3.5" />
              Export CSV
            </Button>
            <Button variant="outline" disabled={!canRun} onClick={() => setSavingAs(true)}>
              <Save className="me-1.5 h-3.5 w-3.5" />
              Save
            </Button>
            <Button disabled={!canRun || run.isPending} onClick={() => run.mutate(definition)}>
              <Play className="me-1.5 h-3.5 w-3.5" />
              Run
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Saved reports</CardTitle>
            </CardHeader>
            <CardContent className="space-y-0.5">
              {(saved ?? []).length === 0 && (
                <p className="py-3 text-sm text-muted-foreground">Nothing saved yet.</p>
              )}
              {(saved ?? []).map((report) => (
                <div
                  key={report.id}
                  className={cn(
                    'group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-muted',
                    loadedId === report.id && 'bg-muted font-medium',
                  )}
                >
                  <button onClick={() => loadSaved(report)} className="min-w-0 flex-1 truncate text-start">
                    {report.name}
                  </button>
                  {report.schedules?.some((schedule) => schedule.isActive) && (
                    <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
                  )}
                  <button
                    onClick={() => setScheduling(report)}
                    className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                    title="Schedule"
                  >
                    <Clock className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => removeReport.mutate(report.id)}
                    className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Builder
            sources={sources ?? []}
            source={source}
            definition={definition}
            chartType={chartType}
            onChange={setDefinition}
            onChartType={setChartType}
          />

          <SchedulesCard />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {run.data ? run.data.source.label : 'Result'}
            </CardTitle>
            <CardDescription>
              {run.data
                ? `${run.data.meta.returned} of ${run.data.meta.total} rows${
                    run.data.meta.truncated ? ` — capped at ${run.data.meta.cap}` : ''
                  }`
                : 'Pick a source and some columns, then run it.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {run.isPending && <TableSkeleton rows={8} columns={5} />}
            {run.isError && <FormError message={(run.error as ApiRequestError).message} />}

            {run.data && chartType !== 'table' && run.data.groups && (
              <div className="mb-6 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  {chartType === 'pie' ? (
                    <PieChart>
                      <Pie
                        data={run.data.groups}
                        dataKey="count"
                        nameKey="key"
                        outerRadius={100}
                        label={(entry: { key: string }) => entry.key}
                        labelLine={false}
                      >
                        {run.data.groups.map((_group, index) => (
                          <Cell key={index} fill={CHART_COLOURS[index % CHART_COLOURS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  ) : chartType === 'line' ? (
                    <LineChart data={run.data.groups}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                      <XAxis dataKey="key" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#1e40af" strokeWidth={2} />
                    </LineChart>
                  ) : (
                    <BarChart data={run.data.groups}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                      <XAxis dataKey="key" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#1e40af" maxBarSize={44} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            )}

            {run.data?.groups && (
              <div className="mb-6">
                <p className="eyebrow mb-2 text-[11px] text-muted-foreground">
                  Grouped by {definition.groupBy}
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Group</TableHead>
                      <TableHead className="text-end">Rows</TableHead>
                      {Object.keys(run.data.groups[0] ?? {})
                        .filter((key) => key !== 'key' && key !== 'count')
                        .map((key) => (
                          <TableHead key={key} className="text-end">
                            {key.replace('_', ' ')}
                          </TableHead>
                        ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {run.data.groups.map((group) => (
                      <TableRow key={group.key}>
                        <TableCell className="text-sm font-medium">{group.key}</TableCell>
                        <TableCell className="tabular text-end">{group.count}</TableCell>
                        {Object.entries(group)
                          .filter(([key]) => key !== 'key' && key !== 'count')
                          .map(([key, value]) => (
                            <TableCell key={key} className="tabular text-end">
                              {String(value)}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {run.data && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {run.data.columns.map((column) => (
                        <TableHead key={column.key}>{column.label}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {run.data.rows.map((row, index) => (
                      <TableRow key={index}>
                        {run.data!.columns.map((column) => (
                          <TableCell
                            key={column.key}
                            className={cn(
                              'text-sm',
                              (column.type === 'money' || column.type === 'number') &&
                                'tabular text-end',
                            )}
                          >
                            {renderCell(row[column.key], column.type)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {run.data.rows.length === 0 && (
                  <p className="py-10 text-center text-sm text-muted-foreground">
                    Nothing matched those filters.
                  </p>
                )}
              </div>
            )}

            {run.data && Object.keys(run.data.totals).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-4 border-t pt-4">
                {Object.entries(run.data.totals).map(([key, value]) => (
                  <div key={key}>
                    <p className="text-xs text-muted-foreground">{key.replace('_', ' ')}</p>
                    <p className="tabular text-sm font-semibold">{String(value)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {savingAs && (
        <SaveDialog
          definition={definition}
          chartType={chartType}
          onClose={() => setSavingAs(false)}
        />
      )}
      {scheduling && <ScheduleDialog report={scheduling} onClose={() => setScheduling(null)} />}
    </div>
  );
}

function Builder({
  sources,
  source,
  definition,
  chartType,
  onChange,
  onChartType,
}: {
  sources: ReportSource[];
  source?: ReportSource;
  definition: ReportDefinition;
  chartType: string;
  onChange: (definition: ReportDefinition) => void;
  onChartType: (value: string) => void;
}) {
  const groupable = source?.fields.filter((field) => field.groupable) ?? [];
  const aggregatable = source?.fields.filter((field) => field.aggregatable) ?? [];

  const toggleField = (key: string) =>
    onChange({
      ...definition,
      fields: definition.fields.includes(key)
        ? definition.fields.filter((field) => field !== key)
        : [...definition.fields, key],
    });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Build</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="report-source">Data</Label>
          <Select
            id="report-source"
            value={definition.dataSource}
            onChange={(event) =>
              onChange({ dataSource: event.target.value, fields: [], filters: [], aggregates: [] })
            }
          >
            <option value="">Choose…</option>
            {sources.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </Select>
          {source && <p className="mt-1 text-xs text-muted-foreground">{source.description}</p>}
        </div>

        {source && (
          <>
            <div>
              <Label>Columns</Label>
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
                {source.fields.map((field) => (
                  <label key={field.key} className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={definition.fields.includes(field.key)}
                      onCheckedChange={() => toggleField(field.key)}
                    />
                    <span className="min-w-0 flex-1 truncate">{field.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <Filters source={source} definition={definition} onChange={onChange} />

            <div>
              <Label htmlFor="report-group">Group by</Label>
              <Select
                id="report-group"
                value={definition.groupBy ?? ''}
                onChange={(event) =>
                  onChange({ ...definition, groupBy: event.target.value || undefined })
                }
              >
                <option value="">No grouping</option>
                {groupable.map((field) => (
                  <option key={field.key} value={field.key}>
                    {field.label}
                  </option>
                ))}
              </Select>
            </div>

            {aggregatable.length > 0 && (
              <div>
                <Label>Totals</Label>
                <div className="space-y-1 rounded-md border p-2">
                  {aggregatable.map((field) => {
                    const on = definition.aggregates?.some(
                      (aggregate) => aggregate.field === field.key,
                    );
                    return (
                      <label key={field.key} className="flex cursor-pointer items-center gap-2 text-sm">
                        <Checkbox
                          checked={Boolean(on)}
                          onCheckedChange={() =>
                            onChange({
                              ...definition,
                              aggregates: on
                                ? (definition.aggregates ?? []).filter(
                                    (aggregate) => aggregate.field !== field.key,
                                  )
                                : [...(definition.aggregates ?? []), { field: field.key, fn: 'sum' }],
                            })
                          }
                        />
                        <span className="min-w-0 flex-1 truncate">Sum of {field.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="report-chart">Show as</Label>
              <Select
                id="report-chart"
                value={chartType}
                onChange={(event) => onChartType(event.target.value)}
              >
                <option value="table">Table</option>
                <option value="bar">Bar chart</option>
                <option value="line">Line chart</option>
                <option value="pie">Pie chart</option>
              </Select>
              {chartType !== 'table' && !definition.groupBy && (
                <p className="mt-1 text-xs text-amber-700">A chart needs a group-by to plot.</p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Filters({
  source,
  definition,
  onChange,
}: {
  source: ReportSource;
  definition: ReportDefinition;
  onChange: (definition: ReportDefinition) => void;
}) {
  const filters = definition.filters ?? [];

  const update = (index: number, patch: Partial<ReportFilter>) =>
    onChange({
      ...definition,
      filters: filters.map((filter, position) =>
        position === index ? { ...filter, ...patch } : filter,
      ),
    });

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label>Filters</Label>
        <button
          onClick={() =>
            onChange({
              ...definition,
              filters: [
                ...filters,
                { field: source.fields[0].key, operator: 'eq' as FilterOperator, value: '' },
              ],
            })
          }
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          <Plus className="me-0.5 inline h-3 w-3" />
          Add
        </button>
      </div>

      <div className="space-y-2">
        {filters.map((filter, index) => {
          const field = source.fields.find((item) => item.key === filter.field);
          const operators = field ? operatorsFor(field.type) : (['eq'] as FilterOperator[]);
          const needsValue = !['is_empty', 'is_not_empty'].includes(filter.operator);

          return (
            <div key={index} className="space-y-1 rounded-md border p-2">
              <div className="flex items-center gap-1">
                <Select
                  value={filter.field}
                  onChange={(event) => update(index, { field: event.target.value, value: '' })}
                  className="h-8 flex-1 text-xs"
                >
                  {source.fields.map((item) => (
                    <option key={item.key} value={item.key}>
                      {item.label}
                    </option>
                  ))}
                </Select>
                <button
                  onClick={() =>
                    onChange({
                      ...definition,
                      filters: filters.filter((_item, position) => position !== index),
                    })
                  }
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>

              <Select
                value={filter.operator}
                onChange={(event) =>
                  update(index, { operator: event.target.value as FilterOperator })
                }
                className="h-8 text-xs"
              >
                {operators.map((operator) => (
                  <option key={operator} value={operator}>
                    {OPERATOR_LABELS[operator]}
                  </option>
                ))}
              </Select>

              {needsValue && (
                <div className="flex gap-1">
                  <Input
                    value={String(filter.value ?? '')}
                    onChange={(event) => update(index, { value: event.target.value })}
                    type={field?.type === 'date' ? 'date' : 'text'}
                    placeholder="Value"
                    className="h-8 text-xs"
                  />
                  {filter.operator === 'between' && (
                    <Input
                      value={String(filter.value2 ?? '')}
                      onChange={(event) => update(index, { value2: event.target.value })}
                      type={field?.type === 'date' ? 'date' : 'text'}
                      placeholder="and"
                      className="h-8 text-xs"
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SchedulesCard() {
  const { data } = useReportSchedules();
  const remove = useDeleteSchedule();

  if (!data || data.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Scheduled</CardTitle>
        <CardDescription>Runs as the report&rsquo;s owner, with their access.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.map((schedule) => (
          <div key={schedule.id} className="flex items-start gap-2 text-sm">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{schedule.report.name}</p>
              <p className="text-xs text-muted-foreground">
                {schedule.frequency.toLowerCase()} ·{' '}
                {schedule.nextRunAt ? `next ${formatDate(schedule.nextRunAt)}` : 'paused'}
              </p>
              {schedule.lastError && (
                <p className="text-xs text-destructive">{schedule.lastError}</p>
              )}
            </div>
            <button
              onClick={() => remove.mutate(schedule.id)}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function SaveDialog({
  definition,
  chartType,
  onClose,
}: {
  definition: ReportDefinition;
  chartType: string;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const save = useSaveReport();

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save this report</DialogTitle>
          <DialogDescription>
            Saved reports appear in your list and can be sent on a schedule.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="save-name">Name</Label>
            <Input
              id="save-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Overdue invoices by client"
            />
          </div>
          <div>
            <Label htmlFor="save-description">What it answers</Label>
            <Input
              id="save-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Everything past its due date, worst first"
            />
          </div>
          {save.isError && <FormError message={(save.error as ApiRequestError).message} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || save.isPending}
            onClick={() =>
              save.mutate(
                { ...definition, name, description, chartType },
                { onSuccess: onClose },
              )
            }
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleDialog({ report, onClose }: { report: SavedReport; onClose: () => void }) {
  const { data: users } = useUserOptions();
  const save = useSaveSchedule();

  const existing = report.schedules?.[0];
  const [frequency, setFrequency] = useState(existing?.frequency ?? 'WEEKLY');
  const [dayOfPeriod, setDayOfPeriod] = useState(1);
  const [hourOfDay, setHourOfDay] = useState(8);
  const [recipients, setRecipients] = useState<string[]>([]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send &ldquo;{report.name}&rdquo; on a schedule</DialogTitle>
          <DialogDescription>
            It runs as you, with your access — so if you lose sight of some of the data, so does the
            email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="sched-frequency">How often</Label>
              <Select
                id="sched-frequency"
                value={frequency}
                onChange={(event) => setFrequency(event.target.value)}
              >
                <option value="DAILY">Every day</option>
                <option value="WEEKLY">Every week</option>
                <option value="MONTHLY">Every month</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="sched-day">
                {frequency === 'WEEKLY' ? 'Day (1 = Mon)' : 'Day of month'}
              </Label>
              <Input
                id="sched-day"
                type="number"
                min={1}
                max={frequency === 'WEEKLY' ? 7 : 28}
                value={dayOfPeriod}
                onChange={(event) => setDayOfPeriod(Number(event.target.value))}
                disabled={frequency === 'DAILY'}
              />
            </div>
            <div>
              <Label htmlFor="sched-hour">Hour (UTC)</Label>
              <Input
                id="sched-hour"
                type="number"
                min={0}
                max={23}
                value={hourOfDay}
                onChange={(event) => setHourOfDay(Number(event.target.value))}
              />
            </div>
          </div>

          <div>
            <Label>Who receives it</Label>
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border p-2">
              {(users ?? []).map((user) => (
                <label key={user.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={recipients.includes(user.id)}
                    onCheckedChange={() =>
                      setRecipients((current) =>
                        current.includes(user.id)
                          ? current.filter((id) => id !== user.id)
                          : [...current, user.id],
                      )
                    }
                  />
                  <span className="min-w-0 flex-1 truncate">{user.name}</span>
                </label>
              ))}
            </div>
          </div>

          {save.isError && <FormError message={(save.error as ApiRequestError).message} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={recipients.length === 0 || save.isPending}
            onClick={() =>
              save.mutate(
                {
                  id: existing?.id,
                  reportId: report.id,
                  frequency,
                  dayOfPeriod,
                  hourOfDay,
                  recipientUserIds: recipients,
                  isActive: true,
                },
                { onSuccess: onClose },
              )
            }
          >
            Schedule it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function renderCell(value: unknown, type: string) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground">—</span>;
  }
  if (type === 'money') return formatMoney(String(value));
  if (type === 'date') return formatDate(String(value));
  if (type === 'minutes') {
    const minutes = Number(value);
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  }
  if (type === 'boolean') return value ? 'Yes' : 'No';
  if (type === 'enum') {
    return <Badge variant="outline">{String(value).replace(/_/g, ' ').toLowerCase()}</Badge>;
  }
  return String(value);
}
